import type { Attachment, Message, Store } from './store.js';

const STORAGE_KEY = 'bokame_chat_state';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_MESSAGES = 50;

type PersistedShape = {
  conversationId: string | null;
  messages: Message[];
  isOpen: boolean;
  emailCaptured: boolean;
  contactEmail: string | null;
  contactName: string | null;
  savedAt: number;
};

const VALID_ROLES = new Set<Message['role']>(['visitor', 'ai', 'agent', 'system']);

/**
 * Valide une pièce jointe issue du localStorage. Même philosophie
 * défensive que `attachments/serialize.ts#parseAttachmentMetas` côté
 * serveur : une entrée non conforme est ignorée plutôt que de faire
 * planter la restauration de tout le message.
 */
function sanitizeAttachment(raw: unknown): Attachment | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string' || typeof o.mime !== 'string' || typeof o.url !== 'string') {
    return null;
  }
  return {
    id: o.id,
    mime: o.mime,
    size: typeof o.size === 'number' ? o.size : 0,
    width: typeof o.width === 'number' ? o.width : null,
    height: typeof o.height === 'number' ? o.height : null,
    url: o.url,
    filename: typeof o.filename === 'string' ? o.filename : null,
  };
}

/**
 * Valide/assainit un message restauré depuis localStorage (D16). Les
 * entrées écrites par une version antérieure du widget (ou simplement sans
 * pièce jointe) n'ont pas de champ `attachments` — c'est un cas normal, pas
 * une anomalie, donc toujours accepté (compat ascendante). Un message dont
 * les champs de base sont manquants/invalides est rejeté entièrement ; une
 * pièce jointe malformée à l'intérieur d'un message par ailleurs valide est
 * simplement ignorée plutôt que de faire planter toute la restauration de
 * l'historique.
 */
function sanitizeMessage(raw: unknown): Message | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (
    typeof o.id !== 'string' ||
    typeof o.role !== 'string' ||
    !VALID_ROLES.has(o.role as Message['role']) ||
    typeof o.content !== 'string' ||
    typeof o.createdAt !== 'number'
  ) {
    return null;
  }
  const message: Message = {
    id: o.id,
    role: o.role as Message['role'],
    content: o.content,
    createdAt: o.createdAt,
  };
  if (typeof o.failed === 'boolean') message.failed = o.failed;
  if (Array.isArray(o.attachments)) {
    const attachments = o.attachments
      .map(sanitizeAttachment)
      .filter((a): a is Attachment => a !== null);
    if (attachments.length > 0) message.attachments = attachments;
  }
  return message;
}

// RGPD (contrat 2.1) : cette fonction ne doit jamais être appelée avant le
// premier clic du visiteur sur le bouton flottant — c'est à l'appelant
// (App.tsx) de le garantir, pas à ce module.
export function loadPersistedState(): Partial<PersistedShape> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedShape;
    if (typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      return null;
    }
    if (Array.isArray(parsed.messages)) {
      parsed.messages = parsed.messages
        .map(sanitizeMessage)
        .filter((m): m is Message => m !== null);
    }
    return parsed;
  } catch {
    return null;
  }
}

// Même règle RGPD que ci-dessus : à n'appeler qu'après le premier clic.
export function savePersistedState(store: Store): void {
  try {
    const payload: PersistedShape = {
      conversationId: store.conversationId.value,
      // Pièces jointes incluses : métadonnées + URL signée uniquement,
      // jamais d'octets (cf. `state/store.ts#Attachment`) — voilà ce qui
      // rend cette persistance viable en localStorage (quelques Ko/message).
      messages: store.messages.value.slice(-MAX_MESSAGES),
      isOpen: store.isOpen.value,
      emailCaptured: store.emailCaptured.value,
      contactEmail: store.contactEmail.value,
      contactName: store.contactName.value,
      savedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* localStorage may be disabled (Safari private) — ignore */
  }
}

export function clearPersistedState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
